import { useSignInMutation } from '@/src/api/rtkQuery/userApi'
import { useAuthForm } from '@/src/hooks/useAuthForm'
import { refreshAuthUser, setAuthUser } from '@/src/redux/slices/authSlice'
import { useAppDispatch } from '@/src/redux/store'
import { invokeCustomEvent } from '@/src/utils/analytics/clevertap'
import { processAuthResponse } from '@/src/utils/authUtils'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ContactsIcon from '@mui/icons-material/Contacts'
import LuggageIcon from '@mui/icons-material/Luggage'
import PercentIcon from '@mui/icons-material/Percent'
import { useTranslations } from 'next-intl'
import React, { useCallback, useState } from 'react'
import { SignInModal } from './SignInModal'
import { useRouter } from '@/src/i18n/navigation'

export interface SignInModalContainerProps {
    open: boolean
    onClose: () => void
    onMenuClose?: () => void
    disableClose?: boolean
}

const benefits = [
    {
        text: 'specialOffers',
        icon: <PercentIcon fontSize="small" />
    },
    {
        text: 'addTravellerInfo',
        icon: <ContactsIcon fontSize="small" />
    },
    {
        text: 'quickCheckout',
        icon: <LuggageIcon fontSize="small" />
    },
    {
        text: 'manageBooking',
        icon: <AssignmentIcon fontSize="small" />
    },
    {
        text: 'immediateRefund',
        icon: <AccountBalanceWalletIcon fontSize="small" />
    }
]

const SignInModalContainer: React.FC<SignInModalContainerProps> = ({ open, onClose, onMenuClose, disableClose }) => {
    const dispatch = useAppDispatch()
    const router = useRouter()
    const [tab, setTab] = useState(0)
    const [forgot, setForgot] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)

    const handleClose = useCallback(() => {
        if (disableClose) return
        setError(null)
        setSuccess(null)
        setForgot(false)
        onClose()
    }, [onClose, disableClose])

    // Handle successful Google Sign-In
    const handleGoogleSignIn = useCallback(
        async (userData: {
            email: string
            firstName: string
            lastName: string
            fullName: string
            picture?: string
            googleId: string
            userId?: string | number
            ctAuth?: string
        }) => {
            try {
                setLoading(true)
                setError(null)
                setSuccess(null)

                console.log('Google Sign-In userData received:', userData)

                // Create auth data object
                const authData = {
                    signedIn: true,
                    email: userData.email,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    fullName: userData.fullName,
                    picture: userData.picture,
                    googleId: userData.googleId,
                    userId: userData.userId,
                    ctAuth: userData.ctAuth
                }

                // Update the auth state with the user data
                dispatch(setAuthUser(authData))

                setSuccess('Successfully signed in with Google!')

                // Close the modal first
                // For strict login wall, we might want to redirect instead of just closing?
                // But the container just handles auth. The parent (header) or middleware handles redirection.
                // If logged in, the middleware will redirect to /flights on next navigation or refresh.
                // But we should probably close the modal here even if disableClose is true, because they successfully signed in.

                // Override disableClose for successful login
                setError(null)
                setSuccess(null)
                setForgot(false)
                onClose()

                // Reset menu state to prevent it from opening automatically
                onMenuClose?.()
            } catch (error) {
                console.error('Error handling Google Sign-In:', error)
                setError('Failed to sign in with Google. Please try again.')
            } finally {
                setLoading(false)
            }
        },
        [dispatch, onClose, onMenuClose]
    )

    // useAuthForm custom hook logic
    const { formState, handlers, validators } = useAuthForm()

    // RTK Query mutations
    const [signIn] = useSignInMutation()

    // Tab change handler
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTab(newValue)
        setForgot(false)
        setError(null)
        setSuccess(null)
    }

    // Sign In logic
    const handleSignIn = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        setLoading(true)
        setError(null)
        setSuccess(null)

        // Validate form
        if (!validators.validateEmail() || !validators.validatePassword()) {
            setError('Please enter valid email and password.')
            setLoading(false)
            return
        }

        try {
            const payload = {
                username: formState.email,
                password: formState.password
            }

            const response = await signIn(payload).unwrap()
            // Process the auth response and update Redux store
            const authData = processAuthResponse(response, dispatch, formState.email)

            // Success - redirect to flights
            if (authData) {
                invokeCustomEvent('CLEVERTAP_PROFILE', { type: 'login' })
                setSuccess('Signed in successfully!')
                // Close the modal first
                // Override disableClose for successful login
                setError(null)
                setSuccess(null)
                setForgot(false)
                onClose()

                // Reset menu state to prevent it from opening automatically
                onMenuClose?.()
                router.push(`/flights`)
            } else {
                setError('Invalid email or password.')
            }
        } catch (error) {
            setError('Something went wrong. Please try again.')
            console.error('Sign in error:', error)
        } finally {
            setLoading(false)
        }
    }



    const handleBackToSignIn = () => {
        setError(null)
        setSuccess(null)
    }

    // Grouped props
    const form = formState
    const status = { loading, error, success, tab, forgot }
    const modalHandlers = {
        onTabChange: handleTabChange,
        onSignIn: handleSignIn,
        onBackToSignIn: handleBackToSignIn,
        onEmailChange: handlers.changeEmailHandler,
        onPasswordChange: handlers.changePasswordHandler,
        onSubmit: handleSignIn,
        setRememberCheckedStatus: handlers.setRememberCheckedStatus,
        handlers
    }

    const t = useTranslations('signInModal')


    return (
        <SignInModal
            open={open}
            onClose={handleClose}
            form={form}
            status={status}
            handlers={modalHandlers}
            benefits={benefits}
            t={t}
            onGoogleSignIn={handleGoogleSignIn}
            disableClose={disableClose}
        />
    )
}

export { SignInModalContainer }
export default SignInModalContainer
